import React from 'react';
import { Clock, X } from 'lucide-react';

/**
 * MobileSlotList - Display list of time slots for mobile
 *
 * Props:
 * - slots: Array - Array of slot objects with start and end times
 * - selectedDate: Date - Currently selected date
 * - onDeleteSlot: (slotId) => void - Callback when slot is deleted
 */
const MobileSlotList = ({ slots = [], selectedDate, onDeleteSlot }) => {
  // Format time for display
  const formatTime = (date) => {
    const h = String(date.getHours()).padStart(2, '0');
    const m = String(date.getMinutes()).padStart(2, '0');
    return `${h}:${m}`;
  };

  // Format date display
  const formatDate = (date) => {
    const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const day = date.getDate();
    return `${dayName}, ${month} ${day}`;
  };

  // Sort slots by start time
  const sortedSlots = [...slots].sort((a, b) => {
    // Handle both formats
    let timeA, timeB;

    if (a.start) {
      timeA = new Date(a.start).getTime();
    } else if (a.date && a.startTime) {
      const [hour, min] = a.startTime.split(':').map(Number);
      const dateA = new Date(a.date);
      dateA.setHours(hour, min, 0, 0);
      timeA = dateA.getTime();
    }

    if (b.start) {
      timeB = new Date(b.start).getTime();
    } else if (b.date && b.startTime) {
      const [hour, min] = b.startTime.split(':').map(Number);
      const dateB = new Date(b.date);
      dateB.setHours(hour, min, 0, 0);
      timeB = dateB.getTime();
    }

    return timeA - timeB;
  });

  return (
    <div className="w-full bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
      {/* Header */}
      <div className="mb-4">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-gray-900 dark:text-white">
            Available Slots
          </h3>
          {slots.length > 0 && (
            <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 text-sm font-medium rounded">
              {slots.length}
            </span>
          )}
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          {formatDate(selectedDate)}
        </p>
      </div>

      {/* Slots list */}
      {slots.length === 0 ? (
        <div className="text-center py-8">
          <div className="text-gray-400 dark:text-gray-600 mb-2">
            <Clock className="w-12 h-12 mx-auto opacity-50" />
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No slots added yet
          </p>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
            Add your first time slot above
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {sortedSlots.map((slot) => {
            // Handle both formats: slot.start/end (Date) or slot.date/startTime/endTime
            let startDate, endDate;

            if (slot.start && slot.end) {
              startDate = new Date(slot.start);
              endDate = new Date(slot.end);
            } else if (slot.date && slot.startTime && slot.endTime) {
              // Parse date and time strings
              const [startHour, startMin] = slot.startTime.split(':').map(Number);
              const [endHour, endMin] = slot.endTime.split(':').map(Number);

              startDate = new Date(slot.date);
              startDate.setHours(startHour, startMin, 0, 0);

              endDate = new Date(slot.date);
              endDate.setHours(endHour, endMin, 0, 0);

              // Handle overnight slots
              if (endHour < startHour) {
                endDate.setDate(endDate.getDate() + 1);
              }
            } else {
              console.error('📱 Invalid slot format:', slot);
              return null;
            }

            return (
              <div
                key={slot.id || slot._id}
                className="
                  flex items-center justify-between gap-3
                  px-4 py-2.5 rounded-lg
                  bg-white dark:bg-gray-700
                  border border-gray-200 dark:border-gray-600
                  hover:border-green-300 dark:hover:border-green-700
                  hover:shadow-sm
                  transition-all duration-200
                "
              >
                {/* Time display with subtle green accent */}
                <div className="flex items-center gap-2.5 flex-1">
                  <div className="w-1 h-8 bg-green-500 rounded-full flex-shrink-0" />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                    {formatTime(startDate)} - {formatTime(endDate)}
                  </span>
                </div>

                {/* Minimalist delete button */}
                <button
                  onClick={() => onDeleteSlot(slot.id || slot._id)}
                  className="
                    flex items-center justify-center
                    w-7 h-7 rounded-md
                    text-gray-400 dark:text-gray-500
                    hover:text-red-500 dark:hover:text-red-400
                    hover:bg-red-50 dark:hover:bg-red-900/20
                    active:scale-95
                    transition-all duration-150
                    touch-manipulation
                  "
                  aria-label="Delete slot"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MobileSlotList;
